import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { notifyHost } from '../../lib/notificationDispatch';
import Sidebar, { SidebarProvider } from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import DiffImages, { diffValuesEqual } from '../../components/DiffImages';
import { format } from 'date-fns';

const STATUS_CONFIG = {
  pending_review: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800 border-green-200' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800 border-red-200' },
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  unpublished: { label: 'Unpublished', color: 'bg-orange-100 text-orange-800 border-orange-200' },
};

const CATEGORY_LABELS = {
  educational: 'Educational',
  sports: 'Sports',
  arts: 'Arts',
  technology: 'Technology',
  outdoor: 'Outdoor',
  indoor: 'Indoor',
  social: 'Social',
  cultural: 'Cultural',
};

const DIFF_FIELDS = [
  { label: 'Title', key: 'title' },
  { label: 'Description', key: 'description' },
  { label: 'Category', key: 'category' },
  { label: 'Location Name', key: 'location_name' },
  { label: 'Location Address', key: 'location_address' },
  { label: 'Start Date', key: 'start_date', isDate: true },
  { label: 'End Date', key: 'end_date', isDate: true },
  { label: 'Registration Deadline', key: 'registration_deadline', isDate: true },
  { label: 'Price', key: 'price' },
  { label: 'Max Participants', key: 'max_participants' },
  { label: 'Min Age', key: 'min_age' },
  { label: 'Max Age', key: 'max_age' },
  { label: 'Event Type', key: 'event_type' },
  { label: 'Difficulty', key: 'difficulty' },
  { label: 'Duration (min)', key: 'duration_minutes' },
  { label: 'Requirements', key: 'requirements' },
  { label: 'What to Bring', key: 'what_to_bring' },
  { label: 'Cancellation Policy', key: 'cancellation_policy' },
  { label: 'Cover Image', key: 'image_url', isImage: true },
  { label: 'Additional Images', key: 'additional_images', isImage: true },
];

const formatDiffValue = (val, isDate) => {
  if (val === null || val === undefined || val === '') return <span className="text-muted-foreground italic">—</span>;
  if (isDate) {
    try { return format(new Date(val), 'MMM d, yyyy h:mm a'); } catch { return String(val); }
  }
  if (typeof val === 'object') return <span className="font-mono text-xs break-all">{JSON.stringify(val)}</span>;
  return String(val);
};

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [pendingEditsExpanded, setPendingEditsExpanded] = useState(true);
  const [pendingEditRejectModal, setPendingEditRejectModal] = useState(false);
  const [pendingEditRejectReason, setPendingEditRejectReason] = useState('');
  const [approveEditConfirmOpen, setApproveEditConfirmOpen] = useState(false);

  const fetchEvent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: sessionData, error: sessionError } = await supabase?.auth?.getSession();
      if (sessionError || !sessionData?.session) {
        setError('You must be logged in as an admin to view event details.');
        setLoading(false);
        return;
      }

      let { data, error: fetchError } = await supabase
        ?.from('events')
        ?.select(`
          id, title, description, category, difficulty, image_url,
          location_name, location_address, start_date, end_date,
          registration_deadline, price, max_participants, min_age, max_age,
          requirements, what_to_bring, cancellation_policy, refund_policy, event_type,
          safety_rating, duration_minutes, status, is_published,
          rejection_reason, is_featured, created_at, updated_at,
          additional_images, ticket_types, recurring_pattern,
          pending_edits, host_id,
          event_host_profiles (
            id, organization_name, full_name, email, avatar_url,
            is_verified, host_type, verification_status
          )
        `)
        ?.eq('id', id)
        ?.single();

      if (fetchError) {
        // Fallback without join
        const fallback = await supabase
          ?.from('events')
          ?.select(`
            id, title, description, category, difficulty, image_url,
            location_name, location_address, start_date, end_date,
            registration_deadline, price, max_participants, min_age, max_age,
            requirements, what_to_bring, cancellation_policy, refund_policy, event_type,
            safety_rating, duration_minutes, status, is_published,
            rejection_reason, is_featured, created_at, updated_at,
            additional_images, ticket_types, recurring_pattern, pending_edits, host_id
          `)
          ?.eq('id', id)
          ?.single();
        if (fallback?.error) throw fallback?.error;
        data = fallback?.data;
      }

      setEvent(data);
    } catch (err) {
      console.error('fetchEvent error:', err);
      setError(err?.message || 'Failed to load event details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleApprove = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      const { error: updateError } = await supabase
        ?.from('events')
        ?.update({
          status: 'approved',
          is_published: true,
          rejection_reason: null,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', event?.id);
      if (updateError) throw updateError;

      const host = event?.event_host_profiles;
      await notifyHost({
        userId: event?.host_id,
        eventId: event?.id,
        notificationType: 'event_approved',
        title: '🎉 Event Approved!',
        body: `"${event?.title}" has been approved and is now live.`,
        emailTemplate: 'event_approval',
        emailTo: host?.email,
        emailSubject: 'Your KidQuest Event Has Been Approved',
        emailData: {
          user_name: host?.full_name || 'there',
          event_name: event?.title || 'Your event',
          event_date: event?.start_date
            ? format(new Date(event.start_date), 'EEEE, d MMMM yyyy')
            : '',
        },
      });

      showSuccess(`"${event?.title}" has been approved and published.`);
      fetchEvent();
    } catch (err) {
      setActionError(err?.message || 'Failed to approve event');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason?.trim()) {
      setActionError('Please provide a rejection reason.');
      return;
    }
    setActionLoading(true);
    setActionError(null);
    try {
      const { error: updateError } = await supabase
        ?.from('events')
        ?.update({
          status: 'rejected',
          is_published: false,
          rejection_reason: rejectionReason?.trim(),
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', event?.id);
      if (updateError) throw updateError;

      const host = event?.event_host_profiles;
      await notifyHost({
        userId: event?.host_id,
        eventId: event?.id,
        notificationType: 'event_rejected',
        title: '❌ Event Not Approved',
        body: `"${event?.title}" was not approved. Reason: ${rejectionReason?.trim()}`,
        emailTemplate: 'event_rejection',
        emailTo: host?.email,
        emailSubject: 'Update on Your KidQuest Event',
        emailData: {
          user_name: host?.full_name || 'there',
          event_name: event?.title || 'Your event',
          rejection_reason: rejectionReason?.trim(),
        },
      });

      showSuccess(`"${event?.title}" has been rejected.`);
      setRejectModalOpen(false);
      setRejectionReason('');
      fetchEvent();
    } catch (err) {
      setActionError(err?.message || 'Failed to reject event');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprovePendingEdits = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      const edits = event?.pending_edits || {};
      const { submitted_at, submitted_by, ...fieldsToApply } = edits;
      const { error: updateError } = await supabase
        ?.from('events')
        ?.update({
          ...fieldsToApply,
          pending_edits: null,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', event?.id);
      if (updateError) throw updateError;

      const approveHost = event?.event_host_profiles;
      await notifyHost({
        userId: event?.host_id,
        eventId: event?.id,
        notificationType: 'event_edit_review',
        title: 'Event Edits Approved',
        body: `Your edits to "${event?.title}" have been approved and are now live.`,
        emailTemplate: 'admin_notification',
        emailTo: approveHost?.email,
        emailSubject: 'Your KidQuest Event Edits Were Approved',
        emailData: {
          user_name: approveHost?.full_name || 'there',
          heading: 'Edits Approved!',
          emoji: '✅',
          intro: 'The KidQuest team reviewed your edits and they are now live.',
          detail_label: 'Event',
          detail_value: event?.title || 'Your event',
        },
      });

      showSuccess(`Edits for "${event?.title}" approved and applied.`);
      fetchEvent();
    } catch (err) {
      setActionError(err?.message || 'Failed to approve pending edits');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectPendingEdits = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      const { error: updateError } = await supabase
        ?.from('events')
        ?.update({
          pending_edits: null,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', event?.id);
      if (updateError) throw updateError;

      const notifBody = pendingEditRejectReason?.trim()
        ? `Your edits to "${event?.title}" were rejected. Reason: ${pendingEditRejectReason?.trim()}`
        : `Your edits to "${event?.title}" were rejected by the admin.`;

      const rejectHost = event?.event_host_profiles;
      await notifyHost({
        userId: event?.host_id,
        eventId: event?.id,
        notificationType: 'event_edit_review',
        title: 'Event Edits Rejected',
        body: notifBody,
        emailTemplate: 'admin_notification',
        emailTo: rejectHost?.email,
        emailSubject: 'Update on Your KidQuest Event Edits',
        emailData: {
          user_name: rejectHost?.full_name || 'there',
          heading: 'Edits Not Approved',
          emoji: '✏️',
          intro: 'The KidQuest team reviewed your edits to this event and did not approve them — the event remains as it was before.',
          detail_label: 'Event',
          detail_value: event?.title || 'Your event',
          detail_label_2: pendingEditRejectReason?.trim() ? 'Reason' : '',
          detail_value_2: pendingEditRejectReason?.trim() || '',
          tone: 'negative',
        },
      });

      showSuccess(`Edits for "${event?.title}" rejected and discarded.`);
      setPendingEditRejectModal(false);
      setPendingEditRejectReason('');
      fetchEvent();
    } catch (err) {
      setActionError(err?.message || 'Failed to reject pending edits');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleFeatured = async () => {
    setActionLoading(true);
    try {
      const { error: updateError } = await supabase
        ?.from('events')
        ?.update({ is_featured: !event?.is_featured, updated_at: new Date()?.toISOString() })
        ?.eq('id', event?.id);
      if (updateError) throw updateError;
      showSuccess(`Featured status updated.`);
      fetchEvent();
    } catch (err) {
      setActionError(err?.message || 'Failed to update featured status');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const statusCfg = STATUS_CONFIG?.[event?.status] || { label: event?.status, color: 'bg-gray-100 text-gray-700 border-gray-200' };

  return (
    <SidebarProvider>
      <div className="flex h-screen bg-background overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden ml-[240px]">
          <main className="flex-1 overflow-y-auto p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <Breadcrumbs items={[
                  { label: 'Event Management', path: '/event-management' },
                  { label: event?.title || 'Event Details', path: `/event-management/${id}` },
                ]} />
                <h1 className="text-3xl font-bold text-foreground mt-2">Event Details</h1>
                <p className="text-muted-foreground mt-1">Review event information and take action</p>
              </div>
              <button
                onClick={() => navigate('/event-management')}
                className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="ArrowLeft" size={14} />
                Back
              </button>
            </div>

            {/* Success Toast */}
            {successMessage && (
              <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm">
                <Icon name="CheckCircle" size={16} />
                {successMessage}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-24">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-muted-foreground">Loading event details...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-24 gap-3">
                <Icon name="AlertCircle" size={32} className="text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
                <button
                  onClick={fetchEvent}
                  className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Retry
                </button>
              </div>
            ) : !event ? (
              <div className="flex flex-col items-center justify-center py-24 gap-3">
                <Icon name="Calendar" size={32} className="text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Event not found</p>
                <button
                  onClick={() => navigate('/event-management')}
                  className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Back to Events
                </button>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto space-y-6">
                {/* Thumbnail */}
                {event?.image_url && (
                  <div className="w-full rounded-2xl overflow-hidden border border-border cursor-pointer" onClick={() => setLightboxImage(event?.image_url)}>
                    <img
                      src={event?.image_url}
                      alt={event?.title}
                      className="w-full h-64 object-cover hover:opacity-90 transition-opacity"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}

                {/* Additional Images */}
                {event?.additional_images && Array.isArray(event?.additional_images) && event?.additional_images?.filter(Boolean)?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Additional Images</p>
                    <div className="grid grid-cols-2 gap-3">
                      {event?.additional_images?.filter(Boolean)?.map((imgUrl, idx) => (
                        <div key={idx} className="w-full rounded-xl overflow-hidden border border-border cursor-pointer" onClick={() => setLightboxImage(imgUrl)}>
                          <img
                            src={imgUrl}
                            alt={`${event?.title} additional image ${idx + 1}`}
                            className="w-full h-48 object-cover hover:opacity-90 transition-opacity"
                            onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Status Badges */}
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusCfg?.color}`}>
                    {statusCfg?.label}
                  </span>
                  {event?.is_published && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
                      <Icon name="Globe" size={12} /> Published
                    </span>
                  )}
                  {event?.is_featured && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                      <Icon name="Star" size={12} /> Featured
                    </span>
                  )}
                </div>

                {/* Rejection Reason */}
                {event?.status === 'rejected' && event?.rejection_reason && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-sm font-medium text-red-800 mb-1">Rejection Reason</p>
                    <p className="text-sm text-red-700">{event?.rejection_reason}</p>
                  </div>
                )}

                {/* Title & Description */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h2 className="text-2xl font-bold text-foreground mb-3">{event?.title}</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">{event?.description}</p>
                </div>

                {/* Details Grid */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wide">Event Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: 'Category', value: CATEGORY_LABELS?.[event?.category] || event?.category || '—' },
                      { label: 'Difficulty', value: event?.difficulty || '—' },
                      { label: 'Event Type', value: event?.event_type || '—' },
                      { label: 'Price', value: event?.price != null ? `LKR ${Number(event?.price)?.toFixed(2)}` : '—' },
                      { label: 'Age Range', value: `${event?.min_age || '?'} – ${event?.max_age || '?'} yrs` },
                      { label: 'Max Participants', value: event?.max_participants || 'Unlimited' },
                      { label: 'Duration', value: event?.duration_minutes ? `${event?.duration_minutes} min` : '—' },
                      { label: 'Safety Rating', value: event?.safety_rating ? `${event?.safety_rating}/5` : '—' },
                    ]?.map(({ label, value }) => (
                      <div key={label} className="bg-muted/30 rounded-lg p-3">
                        <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
                        <p className="text-sm font-medium text-foreground capitalize">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Location */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">Location</h3>
                  <div className="flex items-start gap-3">
                    <Icon name="MapPin" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{event?.location_name || '—'}</p>
                      {event?.location_address && (
                        <p className="text-xs text-muted-foreground mt-0.5">{event?.location_address}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Dates */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">Schedule</h3>
                  <div className="space-y-0 divide-y divide-border">
                    {[
                      { label: 'Start Date', value: formatDateTime(event?.start_date) },
                      { label: 'End Date', value: formatDateTime(event?.end_date) },
                      { label: 'Registration Deadline', value: formatDateTime(event?.registration_deadline) },
                      { label: 'Submitted', value: formatDateTime(event?.created_at) },
                    ]?.map(({ label, value }) => (
                      <div key={label} className="flex justify-between items-center py-3">
                        <span className="text-sm text-muted-foreground">{label}</span>
                        <span className="text-sm font-medium text-foreground">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Host Info */}
                {event?.event_host_profiles && (
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h3 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wide">Host Information</h3>
                    <div className="flex items-center gap-4">
                      {event?.event_host_profiles?.avatar_url ? (
                        <img
                          src={event?.event_host_profiles?.avatar_url}
                          alt="Host avatar"
                          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon name="User" size={20} className="text-primary" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground">
                          {event?.event_host_profiles?.organization_name || event?.event_host_profiles?.full_name || '—'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">{event?.event_host_profiles?.email || '—'}</p>
                        <p className="text-xs text-muted-foreground capitalize mt-0.5">{event?.event_host_profiles?.host_type || ''}</p>
                      </div>
                      {event?.event_host_profiles?.is_verified && (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-200">
                          <Icon name="BadgeCheck" size={12} /> Verified
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Additional Info */}
                {(event?.requirements || event?.what_to_bring) && (
                  <div className="bg-card border border-border rounded-xl p-6 space-y-4">
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Additional Information</h3>
                    {event?.requirements && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Requirements</p>
                        <p className="text-sm text-foreground">{event?.requirements}</p>
                      </div>
                    )}
                    {event?.what_to_bring && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">What to Bring</p>
                        <p className="text-sm text-foreground">{event?.what_to_bring}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Cancellation & Refund Policy */}
                <div className="bg-card border border-border rounded-xl p-6 space-y-4">
                  <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Policies</h3>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name="XCircle" size={14} className="text-muted-foreground" />
                      <p className="text-xs font-medium text-muted-foreground">Cancellation Policy</p>
                    </div>
                    <p className="text-sm text-foreground pl-5">
                      {event?.cancellation_policy || <span className="text-muted-foreground italic">No cancellation policy specified</span>}
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name="RotateCcw" size={14} className="text-muted-foreground" />
                      <p className="text-xs font-medium text-muted-foreground">Refund Policy</p>
                    </div>
                    <p className="text-sm text-foreground pl-5">
                      {event?.refund_policy && event?.refund_policy !== 'no_refund'
                        ? event?.refund_policy
                        : event?.refund_policy === 'no_refund'
                        ? <span className="text-muted-foreground italic">No refund</span>
                        : <span className="text-muted-foreground italic">No refund policy specified</span>
                      }
                    </p>
                  </div>
                </div>

                {/* Recurring Event Details */}
                {event?.recurring_pattern && typeof event?.recurring_pattern === 'object' && Object.keys(event?.recurring_pattern)?.length > 0 && (
                  <div className="bg-card border border-border rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Icon name="RefreshCw" size={15} className="text-primary" />
                      <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Recurring Event</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {event?.recurring_pattern?.frequency && (
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-0.5">Frequency</p>
                          <p className="text-sm font-medium text-foreground capitalize">{event?.recurring_pattern?.frequency}</p>
                        </div>
                      )}
                      {event?.recurring_pattern?.interval && (
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-0.5">Interval</p>
                          <p className="text-sm font-medium text-foreground">Every {event?.recurring_pattern?.interval} {event?.recurring_pattern?.frequency || 'occurrence'}(s)</p>
                        </div>
                      )}
                      {event?.recurring_pattern?.end_date && (
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-0.5">Ends On</p>
                          <p className="text-sm font-medium text-foreground">{formatDateTime(event?.recurring_pattern?.end_date)}</p>
                        </div>
                      )}
                      {event?.recurring_pattern?.occurrences && (
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-0.5">Total Occurrences</p>
                          <p className="text-sm font-medium text-foreground">{event?.recurring_pattern?.occurrences}</p>
                        </div>
                      )}
                      {event?.recurring_pattern?.days_of_week && Array.isArray(event?.recurring_pattern?.days_of_week) && event?.recurring_pattern?.days_of_week?.length > 0 && (
                        <div className="bg-muted/30 rounded-lg p-3 col-span-2">
                          <p className="text-xs text-muted-foreground mb-1">Days of Week</p>
                          <div className="flex flex-wrap gap-1.5">
                            {event?.recurring_pattern?.days_of_week?.map((day, i) => (
                              <span key={i} className="text-xs font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full capitalize">{day}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {event?.recurring_pattern?.time && (
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-0.5">Time</p>
                          <p className="text-sm font-medium text-foreground">{event?.recurring_pattern?.time}</p>
                        </div>
                      )}
                    </div>
                    {/* Render any other keys not explicitly handled */}
                    {Object.entries(event?.recurring_pattern)
                      ?.filter(([key]) => !['frequency','interval','end_date','occurrences','days_of_week','time']?.includes(key))
                      ?.map(([key, val]) => (
                        <div key={key} className="mt-3 bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-0.5 capitalize">{key?.replace(/_/g, ' ')}</p>
                          <p className="text-sm font-medium text-foreground">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</p>
                        </div>
                      ))
                    }
                  </div>
                )}

                {/* Ticket Types */}
                {event?.ticket_types && Array.isArray(event?.ticket_types) && event?.ticket_types?.length > 0 && (
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h3 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wide">Ticket Types</h3>
                    <div className="space-y-3">
                      {event?.ticket_types?.map((ticket, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-foreground">{ticket?.type}</p>
                            {ticket?.description && <p className="text-xs text-muted-foreground">{ticket?.description}</p>}
                          </div>
                          <p className="text-sm font-semibold text-foreground">LKR {Number(ticket?.price || 0)?.toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pending Edits Section */}
                {event?.pending_edits && typeof event?.pending_edits === 'object' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-amber-200">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <Icon name="Clock" size={16} className="text-amber-600" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-amber-900 uppercase tracking-wide">Pending Edits</h3>
                          <p className="text-xs text-amber-700 mt-0.5">
                            Host has submitted edits awaiting review
                            {event?.pending_edits?.submitted_at && (
                              <span className="ml-1">
                                · Submitted {format(new Date(event?.pending_edits?.submitted_at), 'MMM d, yyyy')}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setApproveEditConfirmOpen(true)}
                          disabled={actionLoading}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                        >
                          <Icon name="Check" size={12} />
                          Approve
                        </button>
                        <button
                          onClick={() => { setPendingEditRejectReason(''); setPendingEditRejectModal(true); }}
                          disabled={actionLoading}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                        >
                          <Icon name="X" size={12} />
                          Reject
                        </button>
                        <button
                          onClick={() => setPendingEditsExpanded(!pendingEditsExpanded)}
                          className="p-1.5 rounded-lg text-amber-600 hover:text-amber-800 hover:bg-amber-100 transition-colors"
                          title={pendingEditsExpanded ? 'Collapse' : 'Expand'}
                        >
                          <Icon name={pendingEditsExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Diff Table */}
                    {pendingEditsExpanded && (
                      <div className="px-6 py-4">
                        {(() => {
                          const edits = event?.pending_edits || {};
                          const changedFields = DIFF_FIELDS?.filter(({ key }) => {
                            const current = event?.[key];
                            const proposed = edits?.[key];
                            return proposed !== undefined && !diffValuesEqual(current, proposed);
                          });

                          if (changedFields?.length === 0) {
                            return (
                              <p className="text-sm text-amber-700 italic">No field-level differences detected in the pending edits.</p>
                            );
                          }

                          return (
                            <div className="rounded-lg border border-amber-200 overflow-hidden">
                              <div className="grid grid-cols-[120px_1fr_1fr] bg-amber-100/60 border-b border-amber-200">
                                <div className="px-3 py-2 text-xs font-semibold text-amber-800">Field</div>
                                <div className="px-3 py-2 text-xs font-semibold text-red-600 bg-red-50/50">Current (Live)</div>
                                <div className="px-3 py-2 text-xs font-semibold text-green-700 bg-green-50/50">Proposed (Edit)</div>
                              </div>
                              <table className="w-full bg-white">
                                <tbody>
                                  {changedFields?.map(({ label, key, isDate, isImage }) => {
                                    const current = event?.[key];
                                    const proposed = edits?.[key];
                                    return (
                                      <tr key={key} className="border-b border-amber-100 last:border-0">
                                        <td className="py-2.5 px-3 text-xs font-medium text-muted-foreground w-32 align-top">{label}</td>
                                        <td className="py-2.5 px-3 text-xs text-foreground align-top bg-red-50/50 max-w-[200px]">
                                          {isImage ? <DiffImages value={current} dimmed /> : <span className="line-through opacity-70">{formatDiffValue(current, isDate)}</span>}
                                        </td>
                                        <td className="py-2.5 px-3 text-xs text-foreground align-top bg-green-50/50 max-w-[200px]">
                                          {isImage ? <DiffImages value={proposed} /> : <span className="font-medium text-green-800">{formatDiffValue(proposed, isDate)}</span>}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          );
                        })()}

                        {/* Extra keys not in DIFF_FIELDS */}
                        {(() => {
                          const knownKeys = new Set([...DIFF_FIELDS?.map(f => f?.key), 'submitted_at', 'submitted_by']);
                          const extraKeys = Object.keys(event?.pending_edits || {})?.filter(k => !knownKeys?.has(k));
                          if (extraKeys?.length === 0) return null;
                          return (
                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <p className="text-xs font-medium text-blue-700 mb-1">Additional fields in pending edits:</p>
                              <div className="flex flex-wrap gap-1">
                                {extraKeys?.map(k => (
                                  <span key={k} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded font-mono">{k}</span>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {/* Action Error */}
                {actionError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                    <Icon name="AlertCircle" size={14} />
                    {actionError}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wide">Actions</h3>
                  <div className="flex flex-wrap gap-3">
                    {event?.status === 'pending_review' && (
                      <>
                        <button
                          onClick={handleApprove}
                          disabled={actionLoading}
                          className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium text-sm disabled:opacity-50"
                        >
                          {actionLoading ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Icon name="CheckCircle" size={16} />
                          )}
                          Approve & Publish
                        </button>
                        <button
                          onClick={() => { setRejectModalOpen(true); setActionError(null); }}
                          disabled={actionLoading}
                          className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium text-sm disabled:opacity-50"
                        >
                          <Icon name="XCircle" size={16} />
                          Reject
                        </button>
                      </>
                    )}
                    <button
                      onClick={event?.status === 'approved' ? handleToggleFeatured : undefined}
                      disabled={actionLoading || event?.status !== 'approved'}
                      title={event?.status !== 'approved' ? 'Only approved events can be featured' : ''}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                        event?.status !== 'approved' ?'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-60'
                          : event?.is_featured
                          ? 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100' :'bg-muted text-muted-foreground border-border hover:bg-muted/80'
                      }`}
                    >
                      <Icon name="Star" size={14} />
                      {event?.is_featured ? 'Remove Featured' : 'Mark as Featured'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
      {/* Reject Modal */}
      {rejectModalOpen && event && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white border border-border rounded-2xl w-full max-w-md shadow-xl">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">Reject Event</h3>
              <button
                onClick={() => { setRejectModalOpen(false); setActionError(null); }}
                className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                You are rejecting <span className="font-medium text-foreground">"{event?.title}"</span>. Please provide a reason so the host can fix and resubmit.
              </p>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Rejection Reason <span className="text-destructive">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e?.target?.value)}
                  placeholder="Explain why this event is being rejected..."
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                />
              </div>
              {actionError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  <Icon name="AlertCircle" size={14} />
                  {actionError}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => { setRejectModalOpen(false); setActionError(null); }}
                  className="flex-1 px-4 py-2.5 text-sm font-medium border border-border rounded-xl hover:bg-muted transition-colors text-foreground"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectSubmit}
                  disabled={actionLoading || !rejectionReason?.trim()}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Icon name="XCircle" size={14} />
                  )}
                  Confirm Rejection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approve Pending Edits Confirmation Modal */}
      {approveEditConfirmOpen && event && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white border border-border rounded-2xl w-full max-w-md shadow-xl">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Icon name="CheckCircle" size={18} className="text-green-600" />
                </div>
                <h3 className="text-base font-semibold text-foreground">Approve Pending Edits?</h3>
              </div>
              <button
                onClick={() => setApproveEditConfirmOpen(false)}
                className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                You are about to approve the pending edits for{' '}
                <span className="font-medium text-foreground">"{event?.title}"</span>.
                The proposed changes will be applied to the live event immediately and the host will be notified.
              </p>
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                This action cannot be undone. Are you sure you want to proceed?
              </p>
              {actionError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  <Icon name="AlertCircle" size={14} />
                  {actionError}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => setApproveEditConfirmOpen(false)}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2.5 text-sm font-medium border border-border rounded-xl hover:bg-muted transition-colors text-foreground"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => { await handleApprovePendingEdits(); setApproveEditConfirmOpen(false); }}
                  disabled={actionLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Icon name="Check" size={14} />
                  )}
                  Yes, Approve Edits
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending Edit Reject Modal */}
      {pendingEditRejectModal && event && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white border border-border rounded-2xl w-full max-w-md shadow-xl">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">Reject Pending Edits</h3>
              <button
                onClick={() => setPendingEditRejectModal(false)}
                className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                Rejecting edits for <span className="font-medium text-foreground">"{event?.title}"</span>. The event will remain unchanged. Optionally provide a reason to notify the host.
              </p>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Reason <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <textarea
                  value={pendingEditRejectReason}
                  onChange={(e) => setPendingEditRejectReason(e?.target?.value)}
                  placeholder="Explain why these edits are being rejected..."
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                />
              </div>
              {actionError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  <Icon name="AlertCircle" size={14} />
                  {actionError}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => setPendingEditRejectModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium border border-border rounded-xl hover:bg-muted transition-colors text-foreground"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectPendingEdits}
                  disabled={actionLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Icon name="XCircle" size={14} />
                  )}
                  Reject Edits
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Overlay */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors"
            onClick={() => setLightboxImage(null)}
            aria-label="Close image"
          >
            <Icon name="X" size={24} />
          </button>
          <img
            src={lightboxImage}
            alt="Full size preview"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-xl shadow-2xl"
            onClick={(e) => e?.stopPropagation()}
          />
        </div>
      )}
    </SidebarProvider>
  );
};

export default EventDetails;
